import {
  DataFieldWithDataType,
  getKeyFromDataFieldWithDataType,
  toAdvancedDimension,
} from '../../../../../common-types';
import {
  ApiMasterDataAdvancedQuery,
  ApiMasterDataQueryResponse,
} from '../../../../common/api/api-interfaces';
import { CachedMasterDataService } from '../../../../common/api/cached-master-data-service';
import { Months } from '../../../../common/date-manager/date-manager-constants';
import { dateManagerService } from '../../../../common/date-manager/date-manager-service';
import { PAST_PRESENT_TEMPORALITY_FILTER } from '../../../../common/filter/common-filters';
import { getVersionFilterFromDate } from '../../../../common/filter/utils';
import { GetResponseForAdvancedQueryService } from '../../../../common/services/GetResponseForQueryService';
import { VersionId } from '../../../../common/v2/api/types';
import {
  DataTypes,
  EmployeeDataFields,
  Operations,
} from '../../../../common/v2/types';
import { PINNED_FIELD } from '../drilldownColumns';
import { EnrichmentCacheEntry } from '../types';

export interface PbsDrillDownEmployeeEntry {
  id: string;
  /** Attribute values keyed by getKeyFromDataFieldWithDataType(field). */
  values: EnrichmentCacheEntry;
}

function getDimValue(
  dimensions: ApiMasterDataQueryResponse['dataPoints'][0]['dimensions'],
  property: string
): string | undefined {
  const match = dimensions?.find((dim) => dim.property === property);
  return match?.value != null ? String(match.value) : undefined;
}

/**
 * The fields to actually request for a set of selected columns: the employee ID (the row
 * key) plus the selected fields.
 */
export function getDimensionsForFields(
  fields: DataFieldWithDataType[]
): DataFieldWithDataType[] {
  const dimensions = [PINNED_FIELD, ...fields];
  const seen = new Set<string>();
  return dimensions.filter((f) => {
    const key = getKeyFromDataFieldWithDataType(f);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Enriches employee IDs using the same path as the data view: advanced query
 * with VERSION_ID filter (from end date + latest version) and temporality.
 * This returns non-null name/job title when the backend has them for that version.
 *
 * `fields` are the selected columns. Callers must pass only fields the role may read —
 * a field the role has lost access to must never reach the query, so that its value is
 * never fetched rather than being fetched and hidden in the UI.
 */
export async function getEmployeesForPbsDrillDown(
  employeeIds: string[],
  endDate: string,
  firstMonthOfYear: Months,
  latestVersion: VersionId,
  cachedMasterDataApiService: CachedMasterDataService,
  fields: DataFieldWithDataType[]
): Promise<PbsDrillDownEmployeeEntry[]> {
  if (employeeIds.length === 0) return [];
  const { parseApiDate } = dateManagerService.getInstance();
  const versionFilter = getVersionFilterFromDate(
    parseApiDate(endDate),
    firstMonthOfYear,
    latestVersion
  );
  const employeeIdFilter = {
    dataType: DataTypes.EMPLOYEE,
    operation: Operations.EQUAL,
    property: EmployeeDataFields.EMPLOYEE_ID,
    values: employeeIds,
  };
  const dimensionFields = getDimensionsForFields(fields);
  const query: ApiMasterDataAdvancedQuery = {
    dataType: DataTypes.EMPLOYEE,
    dimensions: dimensionFields.map((d) => toAdvancedDimension(d)),
    filterItems: [
      versionFilter,
      PAST_PRESENT_TEMPORALITY_FILTER,
      employeeIdFilter,
    ],
  };
  const result = await GetResponseForAdvancedQueryService(
    query,
    cachedMasterDataApiService
  );
  const dataPoints = result?.dataPoints ?? [];
  // Deduplicate by employee ID: an employee can appear in multiple data points
  // when there are overlapping employment periods. Last-one-wins is fine since
  // all rows for the same employee carry the same point-in-time attributes.
  const seenIds = new Set<string>();
  const employees: PbsDrillDownEmployeeEntry[] = [];
  for (const dataPoint of dataPoints) {
    const dimensions = dataPoint.dimensions ?? [];
    const id = getDimValue(dimensions, EmployeeDataFields.EMPLOYEE_ID) ?? '';
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);

    const values: EnrichmentCacheEntry = {};
    for (const field of dimensionFields) {
      const value = getDimValue(dimensions, field.dataField);
      if (value != null) {
        values[getKeyFromDataFieldWithDataType(field)] = value;
      }
    }

    employees.push({ id, values });
  }
  return employees;
}
